import { useEffect, useRef, useState, useCallback } from 'react';
import { AUTH_EXPIRED_EVENT, AuthExpiredError, authenticatedFetch, isAuthExpired } from '@/utils/authFetch';

/**
 * Interface for cached data entries
 */
interface CacheEntry {
  data: any;
  timestamp: number;
  expiresAt: number;
}

/**
 * React hook to consolidate loading operations across component instances.
 * Ensures that when the same componentId requests a load simultaneously,
 * they share the same Promise instead of duplicating work.
 */
export function useLoadingConsolidation() {
  const inflightRef = useRef<Map<string, Promise<any>>>(new Map());

  const withLoadingConsolidation = useCallback(<T,>(componentId: string, fn: () => Promise<T>): Promise<T> => {
    const existing = inflightRef.current.get(componentId) as Promise<T> | undefined;
    if (existing) return existing;

    const p = fn().finally(() => {
      // Only clear if the stored promise is the same reference
      if (inflightRef.current.get(componentId) === p) {
        inflightRef.current.delete(componentId);
      }
    });
    inflightRef.current.set(componentId, p);
    return p;
  }, []);

  const has = useCallback((componentId: string) => inflightRef.current.has(componentId), []);

  return { withLoadingConsolidation, has } as const;
}

/**
 * Options for cache operations
 */
export interface CacheOptions {
  ttl?: number; // Time to live in milliseconds (default: 300000 = 5 minutes)
  key?: string; // Optional custom cache key
  deduplicate?: boolean; // Enable request deduplication (default: true)
  debounce?: number; // Debounce delay in milliseconds (default: 500)
  forceRefresh?: boolean; // Bypass cache and fetch fresh data
  signal?: AbortSignal; // AbortController signal for request cancellation
}

/**
 * Interface for cache statistics
 */
export interface CacheStats {
  size: number;
  hits: number;
  misses: number;
  hitRate: number;
  evictions: number;
}

const isProtectedApiRequest = (url: string): boolean => url.includes('/api/') && !url.includes('/api/auth/');

let authExpiryListenerRegistered = false;

/**
 * Comprehensive frontend cache utility with TTL, request deduplication, and AbortController management
 */
class RequestCache {
  private cache: Map<string, CacheEntry> = new Map();
  private pendingRequests: Map<string, Promise<any>> = new Map();
  private abortControllers: Map<string, AbortController> = new Map();
  private debounceTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();
  private debounceResolvers: Map<string, Array<{ resolve: (value: any) => void; reject: (error: any) => void }>> = new Map();
  private maxCacheSize: number = 100;
  private stats: CacheStats = {
    size: 0,
    hits: 0,
    misses: 0,
    hitRate: 0,
    evictions: 0,
  };

  /**
   * Generate a unique cache key from URL, method, body, and auth token
   * Format: METHOD:url|hash_suffix for pattern matching
   */
  private generateCacheKey(url: string, options?: RequestInit & CacheOptions): string {
    const method = options?.method || 'GET';
    const body = options?.body ? JSON.stringify(options.body) : '';
    const authToken = typeof window !== 'undefined' ? localStorage.getItem('access_token') : '';

    // Create hash from method + URL + body + auth token to prevent cross-user pollution
    const keyData = `${method}|${url}|${body}|${authToken}`;
    let hash = 0;
    for (let i = 0; i < keyData.length; i++) {
      const char = keyData.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    // Return readable prefix + hash for pattern matching
    const normalizedUrl = url.replace(/\/$/, ''); // Remove trailing slash
    return `${method}:${normalizedUrl}|${Math.abs(hash)}`;
  }

  /**
   * Check if a cache entry is expired
   */
  private isExpired(entry: CacheEntry): boolean {
    return Date.now() > entry.expiresAt;
  }

  /**
   * Evict the oldest cache entry when size limit is reached
   */
  private evictOldest(): void {
    let oldestKey = '';
    let oldestTime = Date.now();

    for (const [key, entry] of this.cache.entries()) {
      if (entry.timestamp < oldestTime) {
        oldestTime = entry.timestamp;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      this.cache.delete(oldestKey);
      this.stats.evictions++;
      this.stats.size = this.cache.size;
    }
  }

  /**
   * Retrieve cached entry if exists and not expired
   */
  get(key: string): CacheEntry | null {
    const entry = this.cache.get(key);
    if (!entry) {
      return null;
    }

    if (this.isExpired(entry)) {
      this.cache.delete(key);
      this.stats.size = this.cache.size;
      return null;
    }

    this.stats.hits++;
    this.updateHitRate();
    return entry;
  }

  /**
   * Store data in cache with expiration
   */
  set(key: string, data: any, ttl: number): void {
    if (this.cache.size >= this.maxCacheSize) {
      this.evictOldest();
    }

    const entry: CacheEntry = {
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + ttl,
    };

    this.cache.set(key, entry);
    this.stats.size = this.cache.size;
  }

  /**
   * Invalidate cache entries matching a pattern
   * Pattern should be like "GET:/api/trading/statistics" or "/api/trading/*"
   */
  invalidate(pattern: string): void {
    // Normalize pattern to match key format
    let normalizedPattern: string;
    if (pattern.startsWith('GET:') || pattern.startsWith('POST:') || pattern.startsWith('PUT:') || pattern.startsWith('DELETE:')) {
      normalizedPattern = pattern;
    } else {
      // If pattern doesn't start with method, assume GET and add wildcard
      normalizedPattern = `GET:${pattern}`;
    }

    const keysToDelete: string[] = [];
    for (const key of this.cache.keys()) {
      // Check if key starts with the pattern (before the |hash part)
      if (key.startsWith(normalizedPattern) || 
          (normalizedPattern.includes('*') && new RegExp(normalizedPattern.replace(/\*/g, '.*')).test(key.split('|')[0]))) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach(key => this.cache.delete(key));
    this.stats.size = this.cache.size;
  }

  /**
   * Clear all cache entries and reset statistics
   */
  clear(): void {
    this.cache.clear();
    this.pendingRequests.clear();
    this.abortControllers.forEach(controller => controller.abort());
    this.abortControllers.clear();
    this.debounceTimers.forEach(timer => clearTimeout(timer));
    this.debounceTimers.clear();
    this.debounceResolvers.clear();

    this.stats = {
      size: 0,
      hits: 0,
      misses: 0,
      hitRate: 0,
      evictions: 0,
    };
  }

  /**
   * Abort a pending request by key
   */
  abort(key: string): void {
    const controller = this.abortControllers.get(key);
    if (controller) {
      controller.abort();
      this.abortControllers.delete(key);
      this.pendingRequests.delete(key);
    }
  }

  /**
   * Get current cache statistics
   */
  getStats(): CacheStats {
    return { ...this.stats };
  }

  /**
   * Reset cache statistics
   */
  resetStats(): void {
    this.stats.hits = 0;
    this.stats.misses = 0;
    this.stats.hitRate = 0;
    this.stats.evictions = 0;
  }

  /**
   * Update hit rate calculation
   */
  private updateHitRate(): void {
    const total = this.stats.hits + this.stats.misses;
    this.stats.hitRate = total > 0 ? this.stats.hits / total : 0;
  }

  /**
   * Warm cache by pre-fetching multiple URLs
   */
  async warmCache(urls: string[]): Promise<void> {
    const promises = urls.map(url =>
      this.cachedFetch(url, { ttl: 300000 })
    );
    await Promise.allSettled(promises);
  }

  /**
   * Export cache data as JSON string
   */
  exportCache(): string {
    const exportData: Record<string, CacheEntry> = {};
    for (const [key, entry] of this.cache.entries()) {
      exportData[key] = entry;
    }
    return JSON.stringify(exportData);
  }

  /**
   * Import cache data from JSON string
   */
  importCache(data: string): void {
    try {
      const importData: Record<string, CacheEntry> = JSON.parse(data);
      for (const [key, entry] of Object.entries(importData)) {
        if (!this.isExpired(entry)) {
          this.cache.set(key, entry);
        }
      }
      this.stats.size = this.cache.size;
    } catch (error) {
      console.warn('Failed to import cache data:', error);
    }
  }

  /**
   * Main fetch wrapper with caching, deduplication, and abort support
   */
  async cachedFetch<T>(url: string, options?: RequestInit & CacheOptions): Promise<T> {
    const {
      ttl = 300000,
      key,
      deduplicate = true,
      debounce = 500,
      forceRefresh = false,
      signal,
      ...fetchOptions
    } = options || {};

    const cacheKey = key || this.generateCacheKey(url, fetchOptions);

    // Check for debouncing
    {
      const method = (fetchOptions.method || 'GET').toUpperCase();
      const allowDebounce = debounce > 0 && !(forceRefresh && method === 'GET');
      if (allowDebounce) {
      return new Promise<T>((resolve, reject) => {
        // Initialize resolvers array for this key if not exists
        if (!this.debounceResolvers.has(cacheKey)) {
          this.debounceResolvers.set(cacheKey, []);
        }
        const resolvers = this.debounceResolvers.get(cacheKey)!;
        resolvers.push({ resolve, reject });

        // Clear existing timer and set new one
        const existingTimer = this.debounceTimers.get(cacheKey);
        if (existingTimer) {
          clearTimeout(existingTimer);
        }

        const timer = setTimeout(async () => {
          this.debounceTimers.delete(cacheKey);
          try {
            const result = await this.performCachedFetch<T>(url, cacheKey, ttl, deduplicate, forceRefresh, fetchOptions, signal);
            // Resolve all queued resolvers
            const resolvers = this.debounceResolvers.get(cacheKey) || [];
            resolvers.forEach(({ resolve }) => resolve(result));
            this.debounceResolvers.delete(cacheKey);
          } catch (error) {
            // Reject all queued rejectors
            const resolvers = this.debounceResolvers.get(cacheKey) || [];
            resolvers.forEach(({ reject }) => reject(error));
            this.debounceResolvers.delete(cacheKey);
          }
        }, debounce);

        this.debounceTimers.set(cacheKey, timer);
      });
      }
    }

    return this.performCachedFetch<T>(url, cacheKey, ttl, deduplicate, forceRefresh, fetchOptions, signal);
  }

  /**
   * Perform the actual cached fetch operation
   */
  private async performCachedFetch<T>(
    url: string,
    cacheKey: string,
    ttl: number,
    deduplicate: boolean,
    forceRefresh: boolean,
    fetchOptions: RequestInit,
    signal?: AbortSignal
  ): Promise<T> {
    const method = (fetchOptions.method || 'GET').toUpperCase();

    if (isAuthExpired() && isProtectedApiRequest(url)) {
      throw new AuthExpiredError();
    }

    // Handle forceRefresh race condition: clear any active debounce timer and capture queued resolvers
    let queuedResolvers: Array<{ resolve: (value: any) => void; reject: (error: any) => void }> | undefined;
    if (forceRefresh) {
      const existingTimer = this.debounceTimers.get(cacheKey);
      if (existingTimer) {
        clearTimeout(existingTimer);
        this.debounceTimers.delete(cacheKey);
      }
      // Capture any queued resolvers to resolve with the fresh result instead of rejecting
      const resolvers = this.debounceResolvers.get(cacheKey);
      if (resolvers && resolvers.length > 0) {
        queuedResolvers = resolvers;
        this.debounceResolvers.delete(cacheKey);
      }
      // Remove any cached entry or pending request to guarantee a fresh fetch
      if (this.cache.delete(cacheKey)) {
        this.stats.size = this.cache.size;
      }
      if (this.pendingRequests.has(cacheKey)) {
        this.pendingRequests.delete(cacheKey);
      }
    }

    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cachedEntry = this.get(cacheKey);
      if (cachedEntry) {
        if (process.env.NODE_ENV === 'development') {
          console.debug(`[Cache HIT] ${url} (${Date.now() - cachedEntry.timestamp}ms old)`);
        }
        return cachedEntry.data;
      }
    }

    // Check for pending request (deduplication)
    if (deduplicate && !(forceRefresh && method === 'GET')) {
      const pendingRequest = this.pendingRequests.get(cacheKey);
      if (pendingRequest) {
        if (process.env.NODE_ENV === 'development') {
          console.debug(`[Cache DEDUPE] ${url} (using pending request)`);
        }
        return pendingRequest;
      }
    }

    this.stats.misses++;
    this.updateHitRate();

    if (process.env.NODE_ENV === 'development') {
      console.debug(`[Cache MISS] ${url} (fetching...)`);
    }

    // Create AbortController if no signal provided
    const controller = signal ? null : new AbortController();
    if (controller) {
      this.abortControllers.set(cacheKey, controller);
    }

    // Prepare fetch options with auth token and abort signal
    const authToken = typeof window !== 'undefined' ? localStorage.getItem('access_token') : '';
    const headers = new Headers(fetchOptions.headers);
    if (authToken && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${authToken}`);
    }

    const finalOptions: RequestInit = {
      ...fetchOptions,
      headers,
      signal: signal || controller?.signal,
    };

    try {
      // Store promise for deduplication
      const fetchPromise = authenticatedFetch(url, finalOptions)
        .then(async response => {
          if (!response.ok) {
            let errorPayload: any = null;
            try {
              errorPayload = await response.json();
            } catch (_e) {
              // non-JSON error response
            }
            const err: any = new Error(
              (errorPayload && (errorPayload.message || errorPayload.error)) || `HTTP ${response.status}: ${response.statusText}`
            );
            err.responseData = errorPayload;
            err.status = response.status;
            throw err;
          }
          return response.json();
        })
        .then(data => {
          // Cache successful GET responses only
          if (fetchOptions.method === 'GET' || !fetchOptions.method) {
            this.set(cacheKey, data, ttl);
          }
          // Resolve any queued debounced resolvers (from forceRefresh) with the fresh result
          if (queuedResolvers && queuedResolvers.length > 0) {
            queuedResolvers.forEach(({ resolve }) => {
              try { resolve(data); } catch (_e) {}
            });
            queuedResolvers = undefined;
          }
          return data;
        })
        .finally(() => {
          // Clean up
          this.pendingRequests.delete(cacheKey);
          if (controller) {
            this.abortControllers.delete(cacheKey);
          }
        });

      this.pendingRequests.set(cacheKey, fetchPromise);
      return await fetchPromise;
    } catch (error: any) {
      // Clean up on error
      this.pendingRequests.delete(cacheKey);
      if (controller) {
        this.abortControllers.delete(cacheKey);
      }

      if (error.name === 'AbortError') {
        // Silent abort - don't throw
        throw error;
      }

      throw error;
    }
  }
}

// Export singleton instance
export const requestCache = new RequestCache();

if (typeof window !== 'undefined' && !authExpiryListenerRegistered) {
  window.addEventListener(AUTH_EXPIRED_EVENT, () => {
    requestCache.clear();
  });
  authExpiryListenerRegistered = true;
}

/**
 * Convenience function for cached GET requests
 */
export async function cachedGet<T>(url: string, options?: CacheOptions): Promise<T> {
  return requestCache.cachedFetch<T>(url, { method: 'GET', ...options });
}

/**
 * Convenience function for cached POST requests (no caching)
 */
export async function cachedPost<T>(url: string, data?: any, options?: CacheOptions): Promise<T> {
  const result = await requestCache.cachedFetch<T>(url, {
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
    headers: { 'Content-Type': 'application/json' },
    // Do not cache POST responses; enable system-wide debouncing/deduplication
    deduplicate: true,
    ...options
  });

  // Invalidate related cache entries after mutations
  if (url.includes('/api/trading/credentials')) {
    requestCache.invalidate('/api/trading/credentials');
    requestCache.invalidate('/api/trading/tier/status');
  } else if (url.includes('/api/trading/risk-settings')) {
    requestCache.invalidate('/api/trading/risk-settings');
  } else if (url.includes('/api/users/profile')) {
    requestCache.invalidate('/api/users/profile');
  } else if (url.includes('/api/referrals')) {
    requestCache.invalidate('/api/referrals');
  }

  return result;
}

/**
 * Convenience function for cached PUT requests (no caching)
 */
export async function cachedPut<T>(url: string, data?: any, options?: CacheOptions): Promise<T> {
  const result = await requestCache.cachedFetch<T>(url, {
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
    headers: { 'Content-Type': 'application/json' },
    deduplicate: true,
    ...options
  });

  // Invalidate related cache entries after mutations
  if (url.includes('/api/trading/credentials')) {
    requestCache.invalidate('/api/trading/credentials');
  } else if (url.includes('/api/trading/risk-settings')) {
    requestCache.invalidate('/api/trading/risk-settings');
  } else if (url.includes('/api/users/profile')) {
    requestCache.invalidate('/api/users/profile');
  } else if (url.includes('/api/referrals')) {
    requestCache.invalidate('/api/referrals');
  }

  return result;
}

/**
 * Convenience function for cached DELETE requests (no caching)
 */
export async function cachedDelete<T>(url: string, options?: CacheOptions): Promise<T> {
  const result = await requestCache.cachedFetch<T>(url, { 
    method: 'DELETE',
    deduplicate: true,
    ...options 
  });

  // Invalidate related cache entries after mutations
  if (url.includes('/api/trading/credentials')) {
    requestCache.invalidate('/api/trading/credentials');
  } else if (url.includes('/api/users/profile')) {
    requestCache.invalidate('/api/users/profile');
  } else if (url.includes('/api/referrals')) {
    requestCache.invalidate('/api/referrals');
  }

  return result;
}

/**
 * React hook for managing AbortController in components
 */
export function useAbortController(): { signal: AbortSignal; abort: () => void } {
  const abortControllerRef = useRef<AbortController | null>(null);
  const [, forceUpdate] = useState(0);

  const getController = () => {
    if (!abortControllerRef.current) {
      abortControllerRef.current = new AbortController();
    }
    return abortControllerRef.current;
  };

  const abort = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    forceUpdate((value: number) => value + 1);
  }, []);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    signal: getController().signal,
    abort,
  };
}
